import type { StepDescriptor } from "@rmnlng/shared";
import { useT, type I18nKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type StepStripProps = {
  steps: readonly StepDescriptor[];
  currentIndex: number;
  completedIndices: ReadonlySet<number>;
};

// Single-letter glyphs per step kind, looked up via i18n so the
// abbreviation tracks the active language (С/П/Ф on RU, C/R/C on RO).
const KIND_GLYPH_KEY: Record<StepDescriptor["kind"], I18nKey> = {
  WORD: "skills.step_glyph_word",
  RULE: "skills.step_glyph_rule",
  CHUNK: "skills.step_glyph_chunk",
};

/**
 * Horizontal segment strip — one chip per step. Visually tracks where
 * the learner is in the run. Per doctrine §8.3 we render only the
 * concrete step chips (no checkpoint segment); the bridge screen
 * `MaterialCollectedScreen` plays the role of the missing 8th visual
 * once the strip is full.
 *
 * Mockup: `design/hifi/scenarios.jsx` `StepStrip` lines ~362–396.
 */
export function StepStrip({ steps, currentIndex, completedIndices }: StepStripProps) {
  const t = useT();
  const glyphFor = (kind: StepDescriptor["kind"]): string => t(KIND_GLYPH_KEY[kind]);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" role="tablist" aria-label="session steps">
      {steps.map((step, i) => {
        const isDone = completedIndices.has(i);
        const isCurrent = i === currentIndex && !isDone;
        return (
          <div
            key={i}
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[11px] font-semibold transition-colors",
              isDone
                ? "border-transparent text-[color:var(--scen-fg)]"
                : isCurrent
                  ? "border-[color:var(--scen-solid)] text-[color:var(--scen-fg)]"
                  : "border-border text-text-3",
            )}
            style={{
              background: isDone ? "var(--scen-bg2)" : isCurrent ? "var(--scen-bg)" : "var(--surface)",
            }}
            aria-current={isCurrent ? "step" : undefined}
            title={step.kind}
          >
            {glyphFor(step.kind)}
          </div>
        );
      })}
    </div>
  );
}
